import { queriesRequirementDefinitions, queriesRequirements } from "@query";
import {
  toWSDateString,
  WSActions,
  WSAlert,
  WSButton,
  WSCard,
  WSControlGroup,
  WSElement,
  WSFlexBox,
  WSList,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  RequirementDefinitionTransitionScope,
  RequirementDefinitionTransitionStrategy,
  RequirementStatus,
  SharedFileRequestConfiguration
} from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback } from "react";

import { WSQueries } from "../../../../../query/WSQuery";
import { StepComponentProps } from "../../../../../shared/components/ModalFlow/types";
import { useGetNow } from "../../../../../shared/hooks/useGetNow";
import { addDays } from "../../../../../utils/dates";
import { useRequirementDefinitionSettings } from "../../../hooks/useRequirementDefinitionSettings";
import { useAboutRequirementVersionsModal } from "../../../modals/useAboutRequirementVersionsModal";
import { useEditRenewalDeadlineModal } from "../../../modals/useEditRenewalDeadlineModal";
import { getBaseEditValues } from "../../../utils/getBaseEditValues";
import { getRequirementDefinitionTemplate } from "../../../utils/getRequirementDefinitionTemplate";
import {
  convertFormApprovalDataToReviewStrategy,
  convertFormDataToExpirationStrategy
} from "../../CreateRequirementDefinitionForm/utils";
import {
  FlowCreateNewVersionSettingsData,
  FlowCreateNewVersionState
} from "../types";

import { ComparisonTable } from "./ComparisonTable";
import { DEFAULT_TRANSITION_DATA } from "./utils/constants";

export const StepSetTransition: React.FC<
  StepComponentProps<FlowCreateNewVersionState>
> = ({ onBack, onClose, onNext, setState, state }) => {
  const now = useGetNow();
  const modalAboutVersions = useAboutRequirementVersionsModal();
  const modalEditRenewalDeadline = useEditRenewalDeadlineModal();

  const transitionData = state.transitionData ?? DEFAULT_TRANSITION_DATA;
  const { renewalDeadlineDays, scope, transitionMethod } = transitionData;

  const setScope = useCallback(
    (value: RequirementDefinitionTransitionScope): void => {
      setState({
        transitionData: {
          ...transitionData,
          scope: value,
          transitionMethod: RequirementDefinitionTransitionStrategy.RENEWAL
        }
      });
    },
    [setState, transitionData]
  );

  const setRequirementVersionTransitionStrategy = useCallback(
    (value: RequirementDefinitionTransitionStrategy): void => {
      setState({
        transitionData: { ...transitionData, transitionMethod: value }
      });
    },
    [setState, transitionData]
  );

  const setRenewalDeadlineDays = useCallback(
    (value: number): void => {
      setState({
        transitionData: { ...transitionData, renewalDeadlineDays: value }
      });
    },
    [setState, transitionData]
  );

  const queryRequirementDefinition =
    queriesRequirementDefinitions.getRequirementDefinition.useQuery({
      requirementDefinitionId: state.requirementDefinitionId
    });

  const querySettingsItem = useRequirementDefinitionSettings({
    dataSourceId: queryRequirementDefinition.data?.dataSourceId,
    requirementType: queryRequirementDefinition.data?.type
  });

  // v1 fetched two lightweight list-size counts (`x-ws-list-size` header, no
  // payload). v3 `listPayeeRequirements` has no size-only response, so both
  // counts are derived client-side from one full fetch of the definition's
  // requirements (accepted breakage: less efficient, but a single round trip).
  const queryDefinitionRequirements =
    queriesRequirements.listPayeeRequirements.useQueryAll(
      {
        filter: {
          requirementDefinitionId: { in: [state.requirementDefinitionId] }
        }
      },
      { enabled: queryRequirementDefinition.isSuccess }
    );

  const allCount = queryDefinitionRequirements.flatData?.length ?? 0;
  // v1 `Incomplete` maps to SDK `PENDING_COMPLETION` only (spec §2) —
  // `PendingReview` was a distinct v1 status, kept separate here too.
  const incompleteCount =
    queryDefinitionRequirements.flatData?.filter(
      requirement => requirement.status === RequirementStatus.PENDING_COMPLETION
    ).length ?? 0;

  const handleSubmit = useCallback((): void => {
    onNext();
  }, [onNext]);

  const { settingsData } = state;

  if (!settingsData) {
    onClose();
  }

  return (
    <WSQueries
      queries={{
        queryDefinitionRequirements,
        queryRequirementDefinition,
        querySettingsItem
      }}
    >
      {({
        queryRequirementDefinitionData: requirementDefinition,
        querySettingsItemData
      }) => {
        const currentVersion = requirementDefinition.version;
        const newVersion = (currentVersion ?? 1) + 1;

        const template = getRequirementDefinitionTemplate(
          requirementDefinition,
          querySettingsItemData
        );

        const currentSettings: FlowCreateNewVersionSettingsData = {
          ...getBaseEditValues(requirementDefinition),
          ...template.form?.getEditSettingsValues(
            template,
            requirementDefinition,
            querySettingsItemData
          )
        };

        const newSettings: FlowCreateNewVersionSettingsData =
          settingsData ?? currentSettings;

        const previewUpdate = {
          ...convertFormDataToExpirationStrategy(newSettings),
          reviewStrategy: convertFormApprovalDataToReviewStrategy({
            contextType: (
              querySettingsItemData as SharedFileRequestConfiguration
            )?.contextType,
            isApprovalRequired: !!newSettings.isApprovalRequired,
            reviewStrategy: newSettings.reviewStrategy!,
            type: requirementDefinition.type!
          }),
          versionScope: scope,
          versionTransitionStrategy: transitionMethod
        };

        return (
          <WSList gap="2XL">
            <WSElement>
              <WSText.Heading2>Set transition</WSText.Heading2>
              <WSText.ParagraphSm color="gray500" mt="XS">
                Set up who if affected and how they will transition to the new
                version of the requirement
              </WSText.ParagraphSm>
            </WSElement>

            <WSCard
              header={{
                label: {
                  helperText:
                    "These settings determine who will be affected by the new version of the requirement",
                  text: "Who is affected?"
                }
              }}
            >
              <WSControlGroup
                chip
                name="scope"
                options={[
                  {
                    helperText: `Apply v${newVersion} to all contractors currently assigned this requirement, plus any new assignments.`,
                    label: `All contractors (${allCount} + new)`,
                    value: RequirementDefinitionTransitionScope.ALL
                  },
                  {
                    helperText: `Apply v${newVersion} to contractors who haven't completed v${currentVersion} yet, plus any new assignments.`,
                    label: `Incomplete contractors (${incompleteCount} + new)`,
                    value:
                      RequirementDefinitionTransitionScope.INCOMPLETE_AND_NEW
                  },
                  {
                    helperText: `Apply v${newVersion} to newly assigned requirements only.`,
                    label: "New requirements only",
                    value: RequirementDefinitionTransitionScope.NEW_ONLY
                  }
                ]}
                type="radio"
                value={scope}
                onChange={value =>
                  setScope(value as RequirementDefinitionTransitionScope)
                }
              />
            </WSCard>

            {scope !== RequirementDefinitionTransitionScope.NEW_ONLY && (
              <WSCard
                header={{
                  label: {
                    helperText:
                      "Choose how and when contractors transition to the new version",
                    text: "How do they transition?"
                  }
                }}
              >
                <WSControlGroup
                  chip
                  name="transitionMethod"
                  options={[
                    {
                      activeDescription: (
                        <WSElement>
                          <WSFlexBox.CenterY justify="space-between">
                            <WSText.ParagraphSm color="gray600">
                              Renewal deadline
                            </WSText.ParagraphSm>
                            <WSButton.Link
                              size="S"
                              onClick={async () => {
                                const result =
                                  await modalEditRenewalDeadline.open({
                                    renewalDeadlineDays
                                  });

                                if (result) {
                                  setRenewalDeadlineDays(
                                    result.renewalDeadlineDays
                                  );
                                }
                              }}
                            >
                              Edit
                            </WSButton.Link>
                          </WSFlexBox.CenterY>

                          <WSText.ParagraphSm mt="S">
                            {toWSDateString(
                              addDays(now, renewalDeadlineDays),
                              "monthDayYear"
                            )}{" "}
                            ({renewalDeadlineDays} days)
                          </WSText.ParagraphSm>
                          <WSText.ParagraphXs color="gray500" mt="L">
                            Default is 30 days. Edit to set a different deadline
                          </WSText.ParagraphXs>
                        </WSElement>
                      ),

                      helperText: `All affected contractors move to v${newVersion} and have until a deadline to complete the requirement. Completed previous versions remains valid until the deadline.`,
                      label: (
                        <WSFlexBox.CenterY
                          fullWidth
                          gap="S"
                          justify="space-between"
                          pb="S"
                          wrap="nowrap"
                        >
                          <WSText.Paragraph>
                            Renewal transition
                          </WSText.Paragraph>
                          <WSPill
                            icon="info-circle"
                            text="Recommended"
                            theme="blue"
                            type="dark"
                          />
                        </WSFlexBox.CenterY>
                      ),
                      value: RequirementDefinitionTransitionStrategy.RENEWAL
                    },
                    {
                      helperText:
                        "The new version takes effect immediately. Contractors' current progress is reset and they must start over.",
                      label: "Immediate transition",
                      value: RequirementDefinitionTransitionStrategy.IMMEDIATE
                    }
                  ]}
                  type="radio"
                  value={transitionMethod}
                  onChange={value =>
                    setRequirementVersionTransitionStrategy(
                      value as RequirementDefinitionTransitionStrategy
                    )
                  }
                />
              </WSCard>
            )}

            <WSCard
              header={{
                label: {
                  text: "Preview the impact",
                  tooltip: {
                    customAction() {
                      modalAboutVersions.open({
                        requirementDefinition,
                        update: previewUpdate
                      });
                    }
                  }
                }
              }}
            >
              <WSList gap="L">
                {scope === RequirementDefinitionTransitionScope.NEW_ONLY ? (
                  <WSAlert
                    icon="info-circle"
                    theme="info"
                    title="New assignments only"
                  >
                    Only newly assigned contractors will receive v{newVersion}.
                    Existing contractors will remain on their current version.
                  </WSAlert>
                ) : (
                  <WSAlert
                    icon="info-circle"
                    theme={
                      transitionMethod ===
                      RequirementDefinitionTransitionStrategy.IMMEDIATE
                        ? "warning"
                        : "info"
                    }
                    title={
                      transitionMethod ===
                      RequirementDefinitionTransitionStrategy.RENEWAL
                        ? "About renewal transition"
                        : "About immediate transition"
                    }
                  >
                    {transitionMethod ===
                    RequirementDefinitionTransitionStrategy.RENEWAL ? (
                      <>
                        Complete contractors by default receive a 30 day grace
                        period to transition to the new version to ensure
                        eligibility continuity.
                      </>
                    ) : (
                      <>
                        New version of the requirement will be incomplete for
                        everyone once it is created, including currently
                        complete contractors. This may affect their eligibility
                        until new requirements are complete.
                      </>
                    )}
                  </WSAlert>
                )}

                <ComparisonTable
                  completeCount={allCount - incompleteCount}
                  currentSettings={currentSettings}
                  currentVersion={currentVersion!}
                  dataSourceSettings={querySettingsItemData}
                  incompleteCount={incompleteCount}
                  newSettings={newSettings}
                  renewalDeadlineDays={renewalDeadlineDays}
                  requirementType={requirementDefinition.type!}
                  scope={scope}
                  transitionMethod={transitionMethod}
                />
              </WSList>
            </WSCard>

            <WSActions
              alignment="fill"
              buttons={[
                {
                  kind: "Secondary",
                  label: "Back",
                  onClick: onBack,
                  type: "button"
                },
                {
                  kind: "Primary",
                  label: "Save & continue",
                  onClick: handleSubmit,
                  type: "button"
                }
              ]}
            />
          </WSList>
        );
      }}
    </WSQueries>
  );
};
